import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import api from "common/api";
import usePageTitle from "common/usePageTitle";

function ForgotPasswordReset() {
  const { uid, token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  usePageTitle("Resetiranje Lozinke");

  const validationSchema = Yup.object().shape({
    new_password: Yup.string()
      .min(8, "Lozinka mora imati najmanje 8 karaktera")
      .required("Unesite novu lozinku"),
    confirm_password: Yup.string()
      .oneOf([Yup.ref("new_password")], "Lozinke se ne podudaraju")
      .required("Potvrdite novu lozinku"),
  });

  const handleSubmit = async (values: { new_password: string }) => {
    setLoading(true);
    try {
      await api.post("/accounts/users/reset_password_confirm/", {
        uid,
        token,
        new_password: values.new_password,
      });
      toast.success("Vaša lozinka je uspješno promijenjena.");
      navigate("/prijava");
    } catch (error) {
      console.error("Password reset error:", error);
      const errorMessage = "Dogodila se greška prilikom resetiranja lozinke.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Row
      className="d-flex justify-content-center align-items-center"
      style={{ height: "95vh" }}
    >
      <Col md={8} lg={6} xs={12}>
        <Card className="shadow px-4">
          <Card.Body>
            <div className="mb-3 mt-md-4">
              <h2 className="fw-bold mb-2 text-center text-uppercase ">
                Resetiranje Lozinke
              </h2>
              <p>
                Unesite svoju novu lozinku kako biste završili proces
                resetiranja.
              </p>
              <div className="mb-3">
                <Formik
                  initialValues={{
                    new_password: "",
                    confirm_password: "",
                  }}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({
                    handleSubmit,
                    handleChange,
                    values,
                    errors,
                    touched,
                  }) => (
                    <Form noValidate onSubmit={handleSubmit}>
                      <Form.Group className="mb-3" controlId="formNewPassword">
                        <Form.Label>Nova Lozinka</Form.Label>
                        <Form.Control
                          type="password"
                          placeholder="Unesite novu lozinku"
                          name="new_password"
                          value={values.new_password}
                          onChange={handleChange}
                          isInvalid={
                            touched.new_password && !!errors.new_password
                          }
                          required
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.new_password}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group
                        className="mb-3"
                        controlId="formConfirmPassword"
                      >
                        <Form.Label>Potvrdite Lozinku</Form.Label>
                        <Form.Control
                          type="password"
                          placeholder="Potvrdite novu lozinku"
                          name="confirm_password"
                          value={values.confirm_password}
                          onChange={handleChange}
                          isInvalid={
                            touched.confirm_password &&
                            !!errors.confirm_password
                          }
                          required
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.confirm_password}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <div className="d-grid">
                        <Button
                          variant="primary"
                          type="submit"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                              />{" "}
                              Resetiranje...
                            </>
                          ) : (
                            "Resetiraj Lozinku"
                          )}
                        </Button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
              <p className="mt-3 text-center">
                <Link to="/prijava" className="text-primary fw-bold">
                  Povratak na prijavu
                </Link>
              </p>
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
}

export default ForgotPasswordReset;
